'use client'

/**
 * DonorBadge (R91, 2026-05-19)
 *
 * Große Visualisierung der 5 Donor-Tiers — fuer die Donate-Seite und
 * Übersichts-Kontexte. Zeigt:
 *   - großes Avatar-Beispiel mit Ring im Tier-Look
 *   - Tier-Name (z.B. "Förderer")
 *   - Beispiel-Betrag direkt auf dem Badge ("ab 50 € einmal")
 *   - Dauer-Hinweis ("∞ dauerhaft" oder "365 Tage Reset")
 *   - kurze Erklärung
 *
 * Stephan-Auftrag: "beispiele müssen auf den donator batches stehen."
 */

import { DonorAvatar, DonorTier } from './donor-avatar'
import { useTranslation } from '@/i18n/client'

interface BadgeData {
  tier: Exclude<DonorTier, 'NONE' | 'SUPPORTER_BLUE' | 'GOENNER_ONETIME'>
  /** i18n-Key fuer den Tier-Namen (z.B. "donate.tierFoerdererLabel") */
  labelKey: string
  /** i18n-Key fuer die Kategorie-Pille ("Digitales Trinkgeld" vs "Foerderbeitrag") */
  kindKey: 'donate.tierKindTrinkgeld' | 'donate.tierKindFoerder'
  amount: string
  /** i18n-Key fuer den Beitrags-Modus (einmal / pro Monat) */
  amountSubKey: string
  durationIsForever: boolean
  /** i18n-Key fuer den langen Beschreibungs-Text */
  descriptionKey: string
  /** Index 0-4 für den illustrierten Pilot-Avatar — jedes Tier bekommt
   *  einen eigenen Charakter, damit die 5 Badges sofort distinkt wirken. */
  pilotIndex: number
}

const BADGES: BadgeData[] = [
  {
    tier: 'SUPPORTER',
    // R91.4 (2026-05-19): kein "ab" mehr — Betrag auf PayPal ist fix.
    amount: '10 €',
    amountSubKey: 'donate.tierAmountOnce',
    durationIsForever: false,
    descriptionKey: 'donate.badgeSupporterDesc',
    labelKey: 'donate.tierSupporterLabel',
    kindKey: 'donate.tierKindTrinkgeld',
    pilotIndex: 0,
  },
  {
    tier: 'UNTERSTUETZER',
    amount: '20 €',
    amountSubKey: 'donate.tierAmountOnce',
    durationIsForever: false,
    descriptionKey: 'donate.badgeUnterstuetzerDesc',
    labelKey: 'donate.tierUnterstuetzerLabel',
    kindKey: 'donate.tierKindTrinkgeld',
    pilotIndex: 1,
  },
  {
    tier: 'FOERDERER',
    amount: '50 €',
    amountSubKey: 'donate.tierAmountOnce',
    durationIsForever: false,
    descriptionKey: 'donate.badgeFoerdererDesc',
    labelKey: 'donate.tierFoerdererLabel',
    kindKey: 'donate.tierKindFoerder',
    pilotIndex: 2,
  },
  {
    tier: 'SUPPORTER_MONTHLY',
    // R91.3 (2026-05-19): kein "ab" — Monats-Plan ist auf PayPal fix.
    amount: '8 €',
    amountSubKey: 'donate.tierAmountMonthly',
    durationIsForever: true,
    descriptionKey: 'donate.badgeSupporterMonthlyDesc',
    labelKey: 'donate.tierSupporterMonthlyLabel',
    kindKey: 'donate.tierKindFoerder',
    pilotIndex: 3,
  },
  {
    tier: 'GOENNER_MONTHLY',
    // R91.3 (2026-05-19): kein "ab" — Monats-Plan ist auf PayPal fix.
    amount: '12 €',
    amountSubKey: 'donate.tierAmountMonthly',
    durationIsForever: true,
    descriptionKey: 'donate.badgeGoennerMonthlyDesc',
    labelKey: 'donate.tierGoennerMonthlyLabel',
    kindKey: 'donate.tierKindFoerder',
    pilotIndex: 4,
  },
]

interface DonorBadgeProps {
  /** Einzelnen Badge anzeigen oder alle 5? */
  tier?: BadgeData['tier']
  /** Größe des Avatars in px. Default 80 */
  avatarSize?: number
  /** Beispiel-Name auf dem Avatar (Default "P") */
  exampleName?: string
  /** className für äusseren Wrapper */
  className?: string
  /** Aktion-Button (z.B. PayPal-Link) */
  onAction?: () => void
  /** Action-Label */
  actionLabel?: string
  /** R91.2 follow-up: wenn gesetzt → eigenes Profilbild im Ring zeigen,
   *  statt des illustrierten Beispiel-Modellpiloten. So sieht der eingeloggte
   *  Pilot direkt wie jeder Ring um SEIN Bild wirkt. */
  userImage?: string | null
  userName?: string | null
}

export function DonorBadge({
  tier, avatarSize = 80, exampleName = 'A', className = '', onAction, actionLabel,
  userImage, userName,
}: DonorBadgeProps) {
  const data = BADGES.find((b) => b.tier === tier)
  if (!data) return null
  return <SingleBadge data={data} avatarSize={avatarSize} exampleName={exampleName} className={className} onAction={onAction} actionLabel={actionLabel} userImage={userImage} userName={userName} />
}

/** Zeigt alle 5 Tiers nebeneinander — fuer die Donate-Page. */
export function DonorBadgeGrid({
  avatarSize = 80,
  exampleName = 'A',
  className = '',
  onSelect,
}: {
  avatarSize?: number
  exampleName?: string
  className?: string
  onSelect?: (tier: BadgeData['tier']) => void
}) {
  const { t } = useTranslation()
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 ${className}`}>
      {BADGES.map((b) => (
        <SingleBadge
          key={b.tier}
          data={b}
          avatarSize={avatarSize}
          exampleName={exampleName}
          onAction={onSelect ? () => onSelect(b.tier) : undefined}
          actionLabel={t('donate.badgeSelectRing')}
        />
      ))}
    </div>
  )
}

function SingleBadge({
  data, avatarSize, exampleName, className = '', onAction, actionLabel,
  userImage, userName,
}: {
  data: BadgeData
  avatarSize: number
  exampleName: string
  className?: string
  onAction?: () => void
  actionLabel?: string
  userImage?: string | null
  userName?: string | null
}) {
  const { t } = useTranslation()
  const isTrinkgeld = data.kindKey === 'donate.tierKindTrinkgeld'
  // R91.2 follow-up: wenn ein eigener User uebergeben wird, dann echtes
  // Profilbild + echter Name verwenden — Pilot sieht so wie SEIN Ring aussieht.
  const useOwnAvatar = !!userName
  const labelText = t(data.labelKey)
  return (
    <div
      className={`relative flex flex-col items-center p-5 rounded-2xl transition-all h-full ${className}`}
      style={{
        background: 'var(--bg-card, var(--award-ink-2))',
        border: '1px solid var(--border, var(--award-line))',
      }}
    >
      {/* Kategorie-Pille ganz oben — Trinkgeld vs. Förderbeitrag */}
      <div
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider mb-3"
        style={{
          background: isTrinkgeld ? 'rgba(96, 165, 250, 0.12)' : 'rgba(242, 201, 78, 0.14)',
          color: isTrinkgeld ? '#60a5fa' : '#f2c94e',
          border: `1px solid ${isTrinkgeld ? 'rgba(96, 165, 250, 0.30)' : 'rgba(242, 201, 78, 0.35)'}`,
          letterSpacing: '0.10em',
        }}
      >
        {t(data.kindKey)}
      </div>

      {/* Avatar mit Beispiel-Ring — eigenes Profilbild (eingeloggt) oder
          illustrierter männlicher Pilot (ausgeloggt). */}
      <div className="mb-3">
        <DonorAvatar
          src={useOwnAvatar ? userImage ?? undefined : undefined}
          name={useOwnAvatar ? (userName as string) : exampleName}
          donorTier={data.tier}
          size={avatarSize}
          daysLeft={!data.durationIsForever ? 365 : null}
          showBadge
          exampleAvatarIndex={useOwnAvatar ? undefined : data.pilotIndex}
        />
      </div>

      {/* Tier-Name — feste min-Höhe damit alle Cards aligned sind.
          R91.7 follow-up: "Monthly"-Tiers werden zwangsweise auf zwei Zeilen
          gesetzt (Supporter wrappt eh natuerlich wegen Laenge, Goenner aber
          nicht — ohne Force waere die Card optisch unbalanciert). */}
      <h3
        className="text-sm md:text-base font-black uppercase text-center mb-1 min-h-[2.4em] flex flex-col items-center justify-center"
        style={{
          color: 'var(--accent, var(--award-gold))',
          letterSpacing: '0.08em',
          lineHeight: 1.15,
        }}
      >
        {labelText.endsWith(' Monthly') ? (
          <>
            <span>{labelText.replace(/ Monthly$/, '')}</span>
            <span>Monthly</span>
          </>
        ) : (
          labelText
        )}
      </h3>

      {/* Großer Beispiel-Betrag — DAS sieht der Pilot zuerst */}
      <div className="text-center mb-3">
        <div
          className="text-2xl md:text-3xl font-black tabular-nums"
          style={{ color: 'var(--text, var(--award-cirrus))', fontFamily: 'var(--font-display)', lineHeight: 1 }}
        >
          {data.amount}
        </div>
        <div className="text-[11px] uppercase tracking-wider mt-1" style={{ color: 'var(--muted, var(--award-cirrus-3))', letterSpacing: '0.10em' }}>
          {t(data.amountSubKey)}
        </div>
      </div>

      {/* Dauer-Pille */}
      <div
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase mb-3"
        style={{
          background: data.durationIsForever
            ? 'rgba(242, 201, 78, 0.15)'
            : 'rgba(96, 165, 250, 0.15)',
          color: data.durationIsForever ? '#f2c94e' : '#60a5fa',
          border: `1px solid ${data.durationIsForever ? 'rgba(242, 201, 78, 0.40)' : 'rgba(96, 165, 250, 0.40)'}`,
          letterSpacing: '0.08em',
        }}
      >
        {data.durationIsForever ? t('donate.badgeDurationForever') : t('donate.badgeDuration365')}
      </div>

      {/* Beschreibung — flex-1 damit der Button unten klebt und alle Cards
          gleich hoch sind ('ein Guss' wie Stephan wollte) */}
      <p className="text-[11px] text-center leading-relaxed mb-4 flex-1" style={{ color: 'var(--muted, var(--award-cirrus-2))' }}>
        {t(data.descriptionKey)}
      </p>

      {/* r19.48.2 (Stephan-Spec 2026-06-29): Action-Button im Glas-Look passend
          zur Tier-Pille oben (Blau für Trinkgeld, Gold für Förderbeitrag).
          Vorher: dunkler Brown aus var(--accent) — passte in Cream-Themes nicht.
          Jetzt: explizite #60a5fa (Blau) / #f2c94e (Gold) als gradient + glow. */}
      {onAction && (
        <button
          type="button"
          onClick={onAction}
          className="donor-cta-pill w-full mt-auto px-4 py-3 rounded-full text-xs font-black uppercase transition-all"
          data-kind={isTrinkgeld ? 'trinkgeld' : 'foerder'}
          style={{ letterSpacing: '0.14em' }}
        >
          {actionLabel || (isTrinkgeld ? t('donate.tierCtaTrinkgeld') : t('donate.tierCtaFoerder'))}
        </button>
      )}
    </div>
  )
}
